/**
 * Cookie Consent Banner (C4.4)
 * GDPR-compliant cookie banner with Accept All / Reject Non-Essential / Customize.
 * Stores consent state in localStorage before any tracking scripts load.
 * Syncs consent to the backend when the user is authenticated.
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import { trpc } from '../lib/trpc';
import { Button } from '@atoncrm/ui';

/** Consent categories */
type ConsentPreferences = {
  essential: boolean;
  analytics: boolean;
  marketing: boolean;
};

export const CONSENT_POLICY_VERSION = '2026-01-08';

const CONSENT_STORAGE_KEY = 'atoncrm_consent';
const CONSENT_DISMISSED_KEY = 'atoncrm_consent_dismissed';

type StoredConsentRecord = {
  preferences: ConsentPreferences;
  savedAt: string;
  version: string;
};

/** Default state: only essential granted */
const DEFAULT_CONSENT: ConsentPreferences = {
  essential: true,
  analytics: false,
  marketing: false,
};

function isConsentPreferences(value: unknown): value is ConsentPreferences {
  const record = value as Partial<ConsentPreferences> | null;
  return (
    !!record &&
    typeof record.essential === 'boolean' &&
    typeof record.analytics === 'boolean' &&
    typeof record.marketing === 'boolean'
  );
}

function isStoredConsentRecord(value: unknown): value is StoredConsentRecord {
  const record = value as Partial<StoredConsentRecord> | null;
  return (
    !!record &&
    typeof record.version === 'string' &&
    typeof record.savedAt === 'string' &&
    isConsentPreferences(record.preferences)
  );
}

export function storeConsentRecord(
  consent: ConsentPreferences,
  now = new Date()
): StoredConsentRecord {
  const record: StoredConsentRecord = {
    preferences: consent,
    savedAt: now.toISOString(),
    version: CONSENT_POLICY_VERSION,
  };

  localStorage.setItem(CONSENT_STORAGE_KEY, JSON.stringify(record));
  return record;
}

/** Read consent from localStorage */
export function getStoredConsent(): ConsentPreferences | null {
  try {
    const stored = localStorage.getItem(CONSENT_STORAGE_KEY);
    if (!stored) return null;
    const parsed = JSON.parse(stored) as unknown;

    if (isStoredConsentRecord(parsed)) {
      return parsed.preferences;
    }

    if (isConsentPreferences(parsed)) {
      return parsed;
    }

    return null;
  } catch {
    return null;
  }
}

/** Check if analytics consent has been granted */
export function hasAnalyticsConsent(): boolean {
  const consent = getStoredConsent();
  return consent?.analytics ?? false;
}

/** Check if marketing consent has been granted */
export function hasMarketingConsent(): boolean {
  const consent = getStoredConsent();
  return consent?.marketing ?? false;
}

export function CookieBanner() {
  const [visible, setVisible] = useState(false);
  const [showCustomize, setShowCustomize] = useState(false);
  const [preferences, setPreferences] = useState<ConsentPreferences>(DEFAULT_CONSENT);
  const bannerRef = useRef<HTMLDivElement | null>(null);

  const updateMutation = trpc.consent.update.useMutation();

  useEffect(() => {
    // Only show banner if consent hasn't been dismissed
    const dismissed = localStorage.getItem(CONSENT_DISMISSED_KEY);
    if (!dismissed) {
      setVisible(true);
    }
    // Load existing preferences if any
    const stored = getStoredConsent();
    if (stored) {
      setPreferences(stored);
    }
  }, []);

  useEffect(() => {
    const root = document.documentElement;

    if (!visible) {
      root.style.removeProperty('--cookie-banner-height');
      return;
    }

    const updateBannerHeight = () => {
      const height = bannerRef.current?.offsetHeight ?? 0;
      root.style.setProperty('--cookie-banner-height', `${height}px`);
    };

    updateBannerHeight();
    const observer =
      typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(updateBannerHeight);
    if (bannerRef.current) observer?.observe(bannerRef.current);
    window.addEventListener('resize', updateBannerHeight);

    return () => {
      observer?.disconnect();
      window.removeEventListener('resize', updateBannerHeight);
      root.style.removeProperty('--cookie-banner-height');
    };
  }, [visible, showCustomize]);

  const saveConsent = useCallback(
    (consent: ConsentPreferences) => {
      // Store locally first (before any async work)
      storeConsentRecord(consent);
      localStorage.setItem(CONSENT_DISMISSED_KEY, 'true');
      setPreferences(consent);
      setVisible(false);

      // Sync to backend (best-effort, don't block the UI)
      updateMutation.mutate(
        { types: consent },
        {
          onError: (err) => {
            console.warn('[Consent] Failed to sync consent to server:', err.message);
          },
        }
      );

      // Dispatch custom event so other components can react
      window.dispatchEvent(new CustomEvent('consent-updated', { detail: consent }));
    },
    [updateMutation]
  );

  const handleAcceptAll = () => {
    saveConsent({ essential: true, analytics: true, marketing: true });
  };

  const handleRejectNonEssential = () => {
    saveConsent({ essential: true, analytics: false, marketing: false });
  };

  const handleSaveCustom = () => {
    saveConsent({ ...preferences, essential: true });
  };

  if (!visible) return null;

  return (
    <>
      {/* Spacer to prevent content from being hidden behind the fixed banner */}
      <div className="h-20 sm:h-16" />
      <div
        ref={bannerRef}
        className="border-border bg-card animate-in slide-in-from-bottom-4 fixed right-0 bottom-0 left-0 z-50 border-t p-4 duration-300"
      >
        <div className="mx-auto max-w-4xl">
          {!showCustomize ? (
            /* Default view */
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex-1">
                <p className="text-foreground text-sm">
                  We use cookies to improve your experience. Essential cookies are always active.
                  You can choose to enable analytics and marketing cookies.
                </p>
                <a
                  href="/privacy"
                  className="text-primary hover:text-primary/80 mt-1 inline-flex min-h-6 min-w-6 items-center text-xs transition-colors"
                >
                  Read our Privacy Policy
                </a>
              </div>
              <div className="flex flex-shrink-0 gap-2">
                <button
                  onClick={() => setShowCustomize(true)}
                  className="border-border text-foreground hover:bg-muted rounded-[var(--crm-control-radius)] border px-4 py-2 text-sm font-medium transition-colors"
                >
                  Customize
                </button>
                <button
                  onClick={handleRejectNonEssential}
                  className="border-border text-foreground hover:bg-muted rounded-[var(--crm-control-radius)] border px-4 py-2 text-sm font-medium transition-colors"
                >
                  Reject Non-Essential
                </button>
                <Button type="button" onClick={handleAcceptAll}>
                  Accept All
                </Button>
              </div>
            </div>
          ) : (
            /* Customize view */
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 id="cookie-preferences-title" className="text-foreground text-sm font-semibold">
                  Cookie Preferences
                </h3>
                <button
                  onClick={() => setShowCustomize(false)}
                  className="text-muted-foreground hover:text-foreground text-xs transition-colors"
                >
                  Back
                </button>
              </div>

              <fieldset aria-labelledby="cookie-preferences-title" className="space-y-3">
                {/* Essential - always on */}
                <div className="border-border flex items-center justify-between rounded-[var(--crm-panel-radius)] border p-3">
                  <div>
                    <p className="text-foreground text-sm font-medium">Essential</p>
                    <p className="text-muted-foreground text-xs">
                      Required for the application to function. Cannot be disabled.
                    </p>
                  </div>
                  <div className="bg-muted text-muted-foreground rounded-full px-3 py-1 text-xs">
                    Always on
                  </div>
                </div>

                {/* Analytics */}
                <label className="border-border hover:bg-muted/50 focus-within:ring-ring flex cursor-pointer items-center justify-between rounded-[var(--crm-panel-radius)] border p-3 transition-colors focus-within:ring-2 focus-within:ring-offset-2 focus-within:outline-none">
                  <div>
                    <span
                      id="cookie-analytics-label"
                      className="text-foreground block text-sm font-medium"
                    >
                      Analytics
                    </span>
                    <span
                      id="cookie-analytics-description"
                      className="text-muted-foreground block text-xs"
                    >
                      Help us understand how you use the application to improve it.
                    </span>
                  </div>
                  <input
                    type="checkbox"
                    checked={preferences.analytics}
                    aria-labelledby="cookie-analytics-label"
                    aria-describedby="cookie-analytics-description"
                    onChange={(e) =>
                      setPreferences((prev) => ({ ...prev, analytics: e.target.checked }))
                    }
                    className="border-border text-primary focus:ring-ring h-4 w-4 rounded-[var(--crm-control-radius)]"
                  />
                </label>

                {/* Marketing */}
                <label className="border-border hover:bg-muted/50 focus-within:ring-ring flex cursor-pointer items-center justify-between rounded-[var(--crm-panel-radius)] border p-3 transition-colors focus-within:ring-2 focus-within:ring-offset-2 focus-within:outline-none">
                  <div>
                    <span
                      id="cookie-marketing-label"
                      className="text-foreground block text-sm font-medium"
                    >
                      Marketing
                    </span>
                    <span
                      id="cookie-marketing-description"
                      className="text-muted-foreground block text-xs"
                    >
                      Allow personalized marketing communications and tracking.
                    </span>
                  </div>
                  <input
                    type="checkbox"
                    checked={preferences.marketing}
                    aria-labelledby="cookie-marketing-label"
                    aria-describedby="cookie-marketing-description"
                    onChange={(e) =>
                      setPreferences((prev) => ({ ...prev, marketing: e.target.checked }))
                    }
                    className="border-border text-primary focus:ring-ring h-4 w-4 rounded-[var(--crm-control-radius)]"
                  />
                </label>
              </fieldset>

              <div className="flex justify-end gap-2">
                <button
                  onClick={handleRejectNonEssential}
                  className="border-border text-foreground hover:bg-muted rounded-[var(--crm-control-radius)] border px-4 py-2 text-sm font-medium transition-colors"
                >
                  Reject Non-Essential
                </button>
                <Button type="button" onClick={handleSaveCustom}>
                  Save Preferences
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
